import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  XCircle,
  Zap,
  Shield,
  Globe,
} from "lucide-react";
import { SeoHead } from "../../seo/SeoHead";
import { buildWebSiteJsonLd, buildBreadcrumbListJsonLd } from "../../seo/site";

const SEO_TITLE = "Raguia vs Dust — Quelle IA pour votre PME française ?";
const SEO_DESCRIPTION =
  "Comparez Raguia et Dust. Découvrez pourquoi Raguia est la solution d'IA souveraine idéale pour les entreprises cherchant la simplicité sans développeur.";

export default function VsDustPage() {
  const jsonLd = [
    buildWebSiteJsonLd(),
    buildBreadcrumbListJsonLd([
      { name: "Accueil", path: "/" },
      { name: "Comparatif", path: "/comparatif" },
      { name: "Raguia vs Dust", path: "/vs/dust" },
    ]),
  ];

  return (
    <div className="bg-stone-50 font-body text-[#010101] selection:bg-[#A43032] selection:text-white min-h-screen pb-20">
      <SeoHead
        title={SEO_TITLE}
        description={SEO_DESCRIPTION}
        jsonLd={jsonLd}
        path="/vs/dust"
      />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 lg:px-8 max-w-7xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-[#A43032] text-sm font-medium mb-6">
          <Zap className="size-4" /> Comparatif Solutions IA B2B
        </div>
        <h1 className="font-heading text-4xl md:text-6xl font-semibold tracking-tight mb-6">
          Raguia vs <span className="text-stone-500">Dust</span>
        </h1>
        <p className="text-xl text-stone-600 max-w-3xl mx-auto mb-10 leading-relaxed">
          Dust est une excellente plateforme pour les équipes techniques
          souhaitant construire des assistants sur mesure. Mais si vous cherchez
          une solution clé en main, hébergée en France, déployable en 5 minutes
          sans écrire une ligne de code,{" "}
          <strong className="text-stone-900">Raguia est fait pour vous.</strong>
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/contact"
            className="w-full sm:w-auto px-8 py-4 bg-[#A43032] text-white rounded-xl font-semibold shadow-lg shadow-red-900/20 hover:bg-red-800 transition-all flex items-center justify-center gap-2"
          >
            Démarrer avec Raguia <ArrowRight className="size-5" />
          </Link>
          <Link
            to="/comparatif"
            className="w-full sm:w-auto px-8 py-4 bg-white text-stone-700 border border-stone-200 rounded-xl font-semibold hover:bg-stone-50 hover:border-stone-300 transition-all text-center"
          >
            Voir tous les comparatifs
          </Link>
        </div>
      </section>

      {/* Table Section */}
      <section className="py-16 px-6 lg:px-8 max-w-5xl mx-auto">
        <div className="bg-white rounded-3xl shadow-sm border border-stone-200 overflow-hidden">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-stone-200">
            <div className="p-8 hidden md:block bg-stone-50">
              <h3 className="font-heading text-lg font-semibold text-stone-900 mb-2">Critères</h3>
              <p className="text-sm text-stone-500">
                Ce qui compte vraiment pour votre PME.
              </p>
            </div>
            <div className="p-8 bg-red-50/30 text-center relative">
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#A43032]"></div>
              <h3 className="font-heading text-2xl font-semibold text-[#A43032] mb-2">Raguia</h3>
              <p className="text-sm text-stone-600">Le clé en main</p>
            </div>
            <div className="p-8 text-center bg-stone-50">
              <h3 className="font-heading text-2xl font-semibold text-stone-600 mb-2">Dust</h3>
              <p className="text-sm text-stone-500">L'outil builder</p>
            </div>
          </div>

          <div className="divide-y divide-stone-100">
            <FeatureRow
              title="Public cible"
              raguia="PME, ETI, directions métier (zéro code)"
              dust="Startups tech, développeurs, équipes produit"
            />
            <FeatureRow
              title="Mise en place"
              raguia="Immédiate (upload de PDF/Word)"
              dust="Nécessite de la configuration technique et des intégrations"
            />
            <FeatureRow
              title="Hébergement"
              raguia="100% France"
              dust="US ou EU"
            />
            <FeatureRow
              title="Complexité technique"
              raguia="Aucune"
              dust="Élevée (création d'assistants, prompts complexes)"
            />
            <FeatureRow
              title="Modèles d'IA"
              raguia="Open-source & européens par défaut (Mistral, etc.)"
              dust="Multi-modèles (OpenAI, Anthropic, Google)"
            />

            <FeatureRow
              title="Abonnement mensuel"
              raguia={<>À partir de <span className="font-mono font-bold">90 €</span> / mois (forfait fixe pour l&rsquo;entreprise)</>}
              dust={<>À partir de <span className="font-mono font-bold">29 $</span> / mois / utilisateur (engagement d&rsquo;équipe minimum)</>}
            />
          </div>
        </div>
      </section>

      {/* Key Advantages */}
      <section className="py-16 px-6 lg:px-8 max-w-7xl mx-auto">
        <h2 className="font-heading text-3xl font-semibold text-center mb-12">
          Pourquoi choisir Raguia plutôt que Dust ?
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
            <div className="size-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
              <Zap className="size-6 text-[#A43032]" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-3">
              Zéro prise de tête technique
            </h3>
            <p className="text-stone-600">
              Avec Dust, vous devez concevoir vos assistants, gérer les
              connexions Notion/Slack/Drive et affiner les prompts. Avec Raguia,
              vous déposez vos documents, et l'IA est prête à répondre
              instantanément sur votre portail sécurisé.
            </p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
            <div className="size-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
              <Shield className="size-6 text-[#A43032]" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-3">Souveraineté des données</h3>
            <p className="text-stone-600">
              Vos données sensibles ne devraient pas traverser l'Atlantique.
              Raguia privilégie un hébergement français et des modèles d'IA
              européens (comme Mistral) pour garantir une conformité totale avec
              vos exigences de sécurité.
            </p>
          </div>
          <div className="bg-white p-8 rounded-2xl border border-stone-200 shadow-sm">
            <div className="size-12 bg-red-100 rounded-xl flex items-center justify-center mb-6">
              <Globe className="size-6 text-[#A43032]" />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-3">Tarification prévisible</h3>
            <p className="text-stone-600">
              Pas de surprise sur la facture. Contrairement aux plateformes
              basées sur la consommation de tokens API ou des licences par
              utilisateur très coûteuses, Raguia propose des forfaits adaptés à
              la taille des PME françaises.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto bg-[#010101] rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 size-64 bg-[#A43032] rounded-full blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>

          <h2 className="font-heading text-3xl md:text-4xl font-semibold mb-6 relative z-10">
            Prêt à déployer l'IA dans votre entreprise ?
          </h2>
          <p className="text-stone-400 text-lg mb-10 max-w-2xl mx-auto relative z-10">
            Passez à l'action dès aujourd'hui. Laissez-nous vous montrer comment
            Raguia peut transformer votre gestion documentaire en quelques
            minutes.
          </p>
          <div className="relative z-10">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#A43032] text-white rounded-xl font-semibold hover:bg-red-600 transition-colors"
            >
              Demander une démo <ArrowRight className="size-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 px-6 text-center max-w-4xl mx-auto border-t border-stone-200">
        <p className="text-xs text-stone-400">
          Ce comparatif est basé sur les informations publiques disponibles à la
          date de sa rédaction. Dust est une marque déposée par Dust Inc. Raguia
          n'est pas affilié à Dust.
        </p>
      </section>
    </div>
  );
}

function FeatureRow({ title, raguia, dust }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 hover:bg-stone-50/50 transition-colors">
      <div className="p-4 md:p-6 text-sm font-semibold text-stone-900 border-b md:border-b-0 md:border-r border-stone-100 flex items-center bg-stone-50 md:bg-transparent">
        {title}
      </div>
      <div className="p-4 md:p-6 text-sm text-stone-700 flex items-center justify-center text-center border-b md:border-b-0 md:border-r border-stone-100 bg-white">
        {raguia}
      </div>
      <div className="p-4 md:p-6 text-sm text-stone-500 flex items-center justify-center text-center bg-white">
        {dust}
      </div>
    </div>
  );
}
